import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { CrewDataTableFrame, CrewDataTablePrompt, ReportTextCell, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { Select, TextInput } from '../../components/ui';
import Badge from '../../components/ui/Badge';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';

type ExpiringCert = {
  id: number;
  expiredDate?: string | null;
  certNumber?: string | null;
  isExpired: boolean;
  daysUntilExpiry: number | null;
  crew: { id: number; lastName: string; firstName: string; crewRef?: string | null };
  certCatalog?: { id: number; name: string; code?: string | null } | null;
};

type StatusFilter = 'all' | 'expired' | 'expiring';

type CertExpiryFilters = {
  withinDays: string;
  statusFilter: StatusFilter;
  query: string;
};

const WITHIN_DAYS_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '365 days' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'expired', label: 'Expired' },
  { value: 'expiring', label: 'Expiring soon' },
];

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  const text = value == null || value === '' ? '—' : String(value);
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{text}</span>;
};

const StatusCell: React.FC<{ row: ExpiringCert }> = ({ row }) => {
  if (row.isExpired) {
    return (
      <Badge variant="danger" className="rounded-md px-2 py-0.5">
        Expired
      </Badge>
    );
  }
  const days = row.daysUntilExpiry;
  return (
    <Badge variant="warning" className="rounded-md px-2 py-0.5">
      {days == null ? 'Expiring' : `${days} day${days === 1 ? '' : 's'}`}
    </Badge>
  );
};

const CertExpiryPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const storageKey = getListStateStorageKey('/crew/certs/expiring');
  const [persisted, setPersisted] = usePersistedListFilters<CertExpiryFilters>(storageKey);
  const [withinDays, setWithinDays] = useState(() => persisted?.withinDays ?? '90');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => persisted?.statusFilter ?? 'all');
  const [query, setQuery] = useState(() => persisted?.query ?? '');

  useEffect(() => {
    setPersisted({ withinDays, statusFilter, query });
  }, [withinDays, statusFilter, query, setPersisted]);

  const { data, error, isLoading, mutate } = useSWR<ExpiringCert[]>(
    `/crew/certs/expiring?withinDays=${encodeURIComponent(withinDays)}`,
    fetcher,
    { revalidateOnFocus: true },
  );

  const rows = useMemo(() => {
    const list = data ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((row) => {
      if (statusFilter === 'expired' && !row.isExpired) return false;
      if (statusFilter === 'expiring' && row.isExpired) return false;
      if (!q) return true;
      const haystack = [
        row.crew.lastName,
        row.crew.firstName,
        row.crew.crewRef,
        row.certCatalog?.name,
        row.certCatalog?.code,
        row.certNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, query, statusFilter]);

  const summary = useMemo(() => {
    const list = data ?? [];
    return {
      total: list.length,
      expired: list.filter((r) => r.isExpired).length,
      expiring: list.filter((r) => !r.isExpired).length};
  }, [data]);

  const columns: Column<ExpiringCert>[] = useMemo(
    () => [
      {
        key: 'crew',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crew.id}/file`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline whitespace-nowrap', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.crew.lastName} {row.crew.firstName}
            {row.crew.crewRef ? (
              <span className={cn('ml-1.5 text-xs font-normal', colors.textMuted)}>({row.crew.crewRef})</span>
            ) : null}
          </Link>
        )},
      {
        key: 'certificate',
        header: 'Certificate',
        sortable: true,
        render: (_v, row) => (
          <span className={cn('text-sm', colors.text)}>
            {row.certCatalog?.name ?? '—'}
            {row.certCatalog?.code ? (
              <span className={cn('ml-1.5 text-xs', colors.textMuted)}>{row.certCatalog.code}</span>
            ) : null}
          </span>
        )},
      {
        key: 'certNumber',
        header: 'Number',
        sortable: true,
        render: (v) => <TextCell value={v} />},
      {
        key: 'expiredDate',
        header: 'Expiry',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDate(String(v)) : null} />},
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (_v, row) => <StatusCell row={row} />},
    ],
    [colors.text, colors.textMuted, colors.primaryFg, listReturnPath],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'Certificate Expiry',
          body: 'Lists crew certificates that have already expired or will expire within the selected day window. Use Status and Search to narrow by expired/expiring and by crew or certificate name.'}}
        onSearch={() => mutate()}
        loading={isLoading}
        context={`Certificates expired or expiring within ${withinDays} days${
          statusFilter !== 'all'
            ? ` · ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`
            : ''
        }`}
      >
        <CrewReportFilterField label="Within days" htmlFor="cert-expiry-within" width="sm">
          <Select
            name="withinDays"
            value={withinDays}
            onChange={(e) => setWithinDays(String(e.target.value))}
            options={WITHIN_DAYS_OPTIONS}
            placeholder="Window…"
            showClearButton={false}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Status" htmlFor="cert-expiry-status" width="sm">
          <Select
            name="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            options={STATUS_OPTIONS}
            showClearButton={false}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Search" htmlFor="cert-expiry-search" width="fill">
          <TextInput
            id="cert-expiry-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Crew, certificate, number…"
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {error ? (
          <CrewDataTablePrompt><span className="text-red-600">
            Could not load expiring certificates.
          </span></CrewDataTablePrompt>
        ) : (
          <DataTable<ExpiringCert>
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="No certificates in this window"
            onRowClick={(row) => openCrew(row.crew.id)}
            actions={(row) => <CrewReportViewAction crewId={row.crew.id} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>

      {!error && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-6 rounded-xl border px-4 py-3 text-sm shadow-sm',
            colors.surface,
            colors.border,
          )}
        >
          <div>
            <span className={colors.textSecondary}>Showing</span>
            <span className={cn('ml-2 font-semibold', colors.text)}>{rows.length}</span>
            {rows.length !== summary.total ? (
              <span className={cn('ml-1', colors.textMuted)}>of {summary.total}</span>
            ) : null}
          </div>
          <div>
            <span className={colors.textSecondary}>Expired</span>
            <span className={cn('ml-2 font-semibold text-red-600')}>{summary.expired}</span>
          </div>
          <div>
            <span className={colors.textSecondary}>Expiring soon</span>
            <span className={cn('ml-2 font-semibold text-amber-600')}>{summary.expiring}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertExpiryPage;
